import { createContext, useCallback, useContext, useMemo, useState } from "react";

/**
 * The scans the user asked for in this tab ("Run signal search", PREC-3240), so every cell of a
 * requested company shows "Looking for signals" until its row says it was scanned after the
 * request. A request for the whole tracker (Select all) is one timestamp for every row.
 *
 * The tracker table owns the state (`useSignalScanRequestsState`) and provides it to its cells,
 * because the table's own selection bar starts searches too.
 */
export interface SignalScanRequests {
	trackerId: number | null;
	requestedAt: (trackerOrganizationId: number) => Date | null;
	markRequested: (target: { ids: number[] } | { all: true }) => void;
}

/** The companies a search is for: chosen ones, or every company of the tracker (Select all). */
export type SignalSearchTarget = { ids: number[] } | { all: true; count: number };

/** What a cell reads: the requests, and the table's one way to start a search (with its confirm). */
export interface SignalScanCellContext extends SignalScanRequests {
	requestSearch: (target: SignalSearchTarget) => void;
}

export const SignalScanRequestsContext = createContext<SignalScanCellContext>({
	trackerId: null,
	requestedAt: () => null,
	markRequested: () => undefined,
	requestSearch: () => undefined,
});

export const useSignalScanRequestsState = (trackerId: number | undefined): SignalScanRequests => {
	const [byId, setById] = useState<Record<number, Date>>({});
	const [allAt, setAllAt] = useState<Date | null>(null);

	const markRequested = useCallback((target: { ids: number[] } | { all: true }) => {
		const now = new Date();

		if ("all" in target) {
			setAllAt(now);

			return;
		}

		setById((previous) => ({
			...previous,
			...Object.fromEntries(target.ids.map((id) => [id, now])),
		}));
	}, []);

	const requestedAt = useCallback(
		(trackerOrganizationId: number) => {
			const own = byId[trackerOrganizationId] ?? null;

			if (!allAt) {
				return own;
			}

			return own && own > allAt ? own : allAt;
		},
		[byId, allAt]
	);

	return useMemo(
		() => ({ trackerId: trackerId ?? null, requestedAt, markRequested }),
		[trackerId, requestedAt, markRequested]
	);
};

export const useSignalScanRequests = () => useContext(SignalScanRequestsContext);
